import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    Skeleton,
} from "@/components/ui/skeleton";
import {
    ArrowLeft, Mail, Phone, Globe2, IdCard, GraduationCap, Save, RotateCcw,
    KeyRound, Plus, Award, Clock, ShieldAlert, CheckCircle2, XCircle,
    AlertTriangle, Sparkles, FileText, StickyNote, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import PresenceIndicator from "@/components/admin/PresenceIndicator";
import { openCertificatePdf } from "@/lib/certificateDownload";
import StudentEnrollmentWizard from "@/components/admin/StudentEnrollmentWizard";
import { RadialProgress } from "@/components/ui/radial-progress";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch { return iso; }
};
const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: "medium", timeStyle: "short",
        });
    } catch { return iso; }
};

export default function AdminStudentDetail() {
    const { studentId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sendingReset, setSendingReset] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [programs, setPrograms] = useState([]);
    const [extensionDecision, setExtensionDecision] = useState(null);

    // Editable form state (mirrors the relevant subset of the dossier)
    const [form, setForm] = useState({
        name: "", email: "", phone: "", country: "", student_id: "",
        residency: "domestic", is_active: true, admin_notes: "",
    });
    const [dirty, setDirty] = useState(false);
    const [emailChangedDialog, setEmailChangedDialog] = useState(null); // { new_email }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [d, p] = await Promise.all([
                api.get(`/admin/students/${studentId}`),
                api.get("/programs", { params: { include_archived: true } })
                    .catch(() => ({ data: [] })),
            ]);
            setData(d.data);
            setPrograms(
                (p.data || []).filter((x) => !x.is_deleted)
                    .sort((a, b) => a.title.localeCompare(b.title)),
            );
            setForm({
                name: d.data.name || "",
                email: d.data.email || "",
                phone: d.data.phone || "",
                country: d.data.country || "",
                student_id: d.data.student_id || "",
                residency: d.data.residency || "domestic",
                is_active: d.data.is_active !== false,
                admin_notes: d.data.admin_notes || "",
            });
            setDirty(false);
        } catch (e) {
            toast.error(
                e?.response?.status === 404
                    ? "Student not found."
                    : "Could not load student profile.",
            );
        } finally {
            setLoading(false);
        }
    }, [studentId]);

    useEffect(() => { load(); }, [load]);

    const update = (patch) => {
        setForm((f) => ({ ...f, ...patch }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const oldEmail = data.email;
            const newEmail = form.email.trim().toLowerCase();
            const emailChanged = newEmail && newEmail !== (oldEmail || "").toLowerCase();
            const { data: u } = await api.patch(`/admin/users/${studentId}`, {
                name: form.name.trim() || undefined,
                email: emailChanged ? newEmail : undefined,
                phone: form.phone.trim() || null,
                country: form.country.trim() || null,
                student_id: form.student_id.trim() || null,
                residency: form.residency,
                is_active: form.is_active,
                admin_notes: form.admin_notes,
            });
            setData((d) => ({ ...d, ...u }));
            setDirty(false);
            toast.success("Saved.");
            if (emailChanged) {
                // Surface the follow-up question front-and-centre so the new
                // email address isn't left without sign-in credentials.
                setEmailChangedDialog({ new_email: newEmail });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const sendReset = async () => {
        setSendingReset(true);
        try {
            await api.post(`/admin/users/${studentId}/send-password-reset`);
            toast.success("Password-reset email sent.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send reset email.");
        } finally {
            setSendingReset(false);
        }
    };

    const decideExtension = async (decision, opts = {}) => {
        try {
            if (decision.mode === "approve") {
                await api.post(
                    `/admin/extension-requests/${decision.req.id}/approve`,
                    { approved_days: Number(opts.days), decision_note: opts.note || undefined },
                );
                toast.success(`Approved ${opts.days} day${opts.days === 1 ? "" : "s"}.`);
            } else {
                await api.post(
                    `/admin/extension-requests/${decision.req.id}/deny`,
                    { decision_note: opts.note || undefined },
                );
                toast.success("Request denied.");
            }
            setExtensionDecision(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save decision.");
        }
    };

    if (loading) {
        return (
            <div className="p-6 space-y-4 max-w-6xl mx-auto">
                <Skeleton className="h-8 w-60" />
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-80 w-full" />
            </div>
        );
    }
    if (!data) return null;

    const pendingExt = data.extension_requests.find((r) => r.status === "pending");

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-student-detail">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-primary"
                    >
                        <Link to="/admin/students" data-testid="back-to-students">
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Students
                        </Link>
                    </Button>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        asChild
                        variant="outline"
                        size="sm"
                        data-testid="open-student-attendance-btn"
                    >
                        <Link to={`/admin/students/${studentId}/attendance`}>
                            <Clock className="h-4 w-4 mr-1" />
                            Daily attendance
                        </Link>
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setWizardOpen(true)}
                        data-testid="enrol-another-btn"
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        Enrol in another program
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={sendReset}
                        disabled={sendingReset}
                        data-testid="send-reset-btn"
                    >
                        {sendingReset ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <KeyRound className="h-4 w-4 mr-1" />
                        )}
                        Send password reset
                    </Button>
                </div>
            </div>

            {/* Identity card */}
            <Card className="surface-card border-border">
                <CardContent className="p-6 flex items-start gap-5 flex-wrap">
                    <div className="h-20 w-20 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-display text-3xl ring-1 ring-primary/15 flex-shrink-0">
                        {data.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1
                                className="font-display text-3xl text-primary"
                                data-testid="student-name"
                            >
                                {data.name || "(no name)"}
                            </h1>
                            <PresenceIndicator lastSeenAt={data.last_seen_at} />
                            {!data.is_active && (
                                <Badge
                                    variant="outline"
                                    className="border-rose-400 text-rose-700 bg-rose-50"
                                >
                                    Deactivated
                                </Badge>
                            )}
                            {pendingExt && (
                                <Badge className="bg-amber-500/15 text-amber-700 border border-amber-500/30 gap-1">
                                    <Clock className="h-3 w-3" />
                                    Extension pending
                                </Badge>
                            )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <InfoChip icon={Mail} label="Email" value={data.email} mono />
                            <InfoChip icon={IdCard} label="Student ID" value={data.student_id || "—"} mono />
                            <InfoChip icon={Phone} label="Phone" value={data.phone || "—"} />
                            <InfoChip icon={Globe2} label="Country" value={data.country || "—"} />
                            <InfoChip icon={GraduationCap} label="Residency" value={data.residency || "—"} capitalize />
                            <InfoChip icon={Clock} label="Joined" value={fmtDate(data.created_at)} />
                            <InfoChip
                                icon={Clock}
                                label="Last seen"
                                value={
                                    data.last_seen_at
                                        ? fmtDateTime(data.last_seen_at)
                                        : "Never signed in"
                                }
                            />
                            {data.must_change_password ? (
                                <InfoChip
                                    icon={KeyRound}
                                    label="Password"
                                    value="Must change on next login"
                                    warn
                                />
                            ) : null}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Inline edit panel */}
            <Card className="border-border" data-testid="student-edit-card">
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                                Edit profile
                            </div>
                            <h2 className="font-display text-xl text-primary mt-0.5">
                                Inline updates
                            </h2>
                        </div>
                        <div className="flex items-center gap-2">
                            {dirty && (
                                <Badge
                                    variant="outline"
                                    className="border-amber-400 text-amber-700 bg-amber-50"
                                >
                                    Unsaved changes
                                </Badge>
                            )}
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => load()}
                                disabled={!dirty || saving}
                            >
                                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                Revert
                            </Button>
                            <Button
                                size="sm"
                                className="bg-primary hover:bg-primary/90"
                                onClick={save}
                                disabled={!dirty || saving}
                                data-testid="save-edits-btn"
                            >
                                {saving ? (
                                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                ) : (
                                    <Save className="h-3.5 w-3.5 mr-1" />
                                )}
                                Save
                            </Button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Full name">
                            <Input
                                value={form.name}
                                onChange={(e) => update({ name: e.target.value })}
                                data-testid="edit-name"
                            />
                        </Field>
                        <Field label="Email (login identity)">
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => update({ email: e.target.value })}
                                placeholder="student@example.com"
                                className="font-mono"
                                data-testid="edit-email"
                            />
                            {form.email.trim().toLowerCase() !== (data.email || "").toLowerCase() && (
                                <p className="text-[11px] text-amber-700 mt-1 flex items-start gap-1">
                                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                    Changing the email also changes the student's
                                    sign-in identity. After you save, you'll be asked
                                    whether to send a password-setup link to the new
                                    address.
                                </p>
                            )}
                        </Field>
                        <Field label="Student ID">
                            <Input
                                value={form.student_id}
                                onChange={(e) => update({ student_id: e.target.value })}
                                className="font-mono"
                                placeholder="HCC-YYYY-NNNN"
                                data-testid="edit-student-id"
                            />
                        </Field>
                        <Field label="Phone">
                            <Input
                                value={form.phone}
                                onChange={(e) => update({ phone: e.target.value })}
                                placeholder="+1 604 555 0123"
                                data-testid="edit-phone"
                            />
                        </Field>
                        <Field label="Country">
                            <Input
                                value={form.country}
                                onChange={(e) => update({ country: e.target.value })}
                                placeholder="Canada"
                                data-testid="edit-country"
                            />
                        </Field>
                        <Field label="Residency">
                            <Select
                                value={form.residency}
                                onValueChange={(v) => update({ residency: v })}
                            >
                                <SelectTrigger data-testid="edit-residency">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="domestic">Domestic</SelectItem>
                                    <SelectItem value="international">International</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Account status">
                            <div className="flex items-center gap-3 h-10 px-3 border border-border rounded-md bg-muted/30">
                                <Switch
                                    checked={form.is_active}
                                    onCheckedChange={(v) => update({ is_active: v })}
                                    data-testid="edit-active-toggle"
                                />
                                <span className="text-sm text-foreground">
                                    {form.is_active
                                        ? "Active — student can sign in"
                                        : "Deactivated — sign-in blocked"}
                                </span>
                            </div>
                        </Field>
                    </div>
                    <div>
                        <Label className="text-sm font-semibold flex items-center gap-1.5">
                            <StickyNote className="h-3.5 w-3.5" />
                            Admin notes{" "}
                            <span className="font-normal text-muted-foreground">
                                (only visible to admins)
                            </span>
                        </Label>
                        <Textarea
                            value={form.admin_notes}
                            onChange={(e) => update({ admin_notes: e.target.value })}
                            rows={3}
                            placeholder="Anything the team should know about this learner."
                            maxLength={4000}
                            data-testid="edit-admin-notes"
                        />
                    </div>
                    {data.updated_at ? (
                        <p className="text-[11px] text-muted-foreground">
                            Last edited {fmtDateTime(data.updated_at)}
                        </p>
                    ) : null}
                </CardContent>
            </Card>

            {/* Enrollments */}
            <Card className="border-border" data-testid="student-enrollments-card">
                <CardContent className="p-6 space-y-3">
                    <div className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-primary" />
                        <h2 className="font-display text-xl text-primary">
                            Enrollments
                        </h2>
                        <Badge
                            variant="outline"
                            className="text-[10px] border-border bg-background"
                        >
                            {data.enrollments.length}
                        </Badge>
                    </div>
                    {data.enrollments.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic">
                            No enrollments yet.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {data.enrollments.map((e) => (
                                <EnrollmentCard key={e.enrollment_id} e={e} />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Certificates */}
            {data.certificates.length > 0 && (
                <Card className="border-border" data-testid="student-certificates-card">
                    <CardContent className="p-6 space-y-3">
                        <div className="flex items-center gap-2">
                            <Award className="h-4 w-4 text-accent" />
                            <h2 className="font-display text-xl text-primary">
                                Certificates
                            </h2>
                        </div>
                        <div className="space-y-2">
                            {data.certificates.map((c) => (
                                <div
                                    key={c.id}
                                    className="flex items-center justify-between rounded-md border border-sky-300/60 bg-sky-50/70 px-3 py-2.5 flex-wrap gap-2"
                                    data-testid={`cert-row-${c.id}`}
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-primary truncate">
                                            {c.program_title}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Issued {fmtDate(c.issued_at)}
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="border-sky-400 text-sky-700"
                                        onClick={() => openCertificatePdf(c.id)}
                                        data-testid={`cert-preview-${c.id}`}
                                    >
                                        <FileText className="h-3.5 w-3.5 mr-1" />
                                        Preview PDF
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Extension requests */}
            {data.extension_requests.length > 0 && (
                <Card className="border-border" data-testid="student-extensions-card">
                    <CardContent className="p-6 space-y-3">
                        <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-primary" />
                            <h2 className="font-display text-xl text-primary">
                                Extension history
                            </h2>
                        </div>
                        <div className="space-y-2">
                            {data.extension_requests.map((r) => (
                                <div
                                    key={r.id}
                                    className={`flex items-start justify-between gap-3 flex-wrap rounded-md border px-3 py-2.5 ${
                                        r.status === "pending"
                                            ? "border-amber-400/60 bg-amber-50/60"
                                            : "border-border bg-muted/20"
                                    }`}
                                    data-testid={`ext-row-${r.id}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-semibold text-foreground truncate">
                                            {r.program_title}{" "}
                                            <span className="text-muted-foreground font-normal">
                                                · requested {r.requested_days}d
                                            </span>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            {fmtDate(r.created_at)}
                                            {r.status === "approved" && r.new_end_date
                                                ? ` · Approved +${r.approved_days}d → ${fmtDate(r.new_end_date)}`
                                                : ""}
                                        </div>
                                        {r.reason ? (
                                            <div className="text-[11px] italic text-muted-foreground mt-0.5">
                                                “{r.reason}”
                                            </div>
                                        ) : null}
                                        {r.decision_note ? (
                                            <div className="text-[11px] text-muted-foreground mt-0.5">
                                                Decision note: {r.decision_note}
                                            </div>
                                        ) : null}
                                    </div>
                                    {r.status === "pending" ? (
                                        <div className="flex items-center gap-1.5">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-sky-500/50 text-sky-700 hover:bg-sky-500/10"
                                                onClick={() => setExtensionDecision({
                                                    req: r, mode: "approve",
                                                })}
                                                data-testid={`ext-approve-${r.id}`}
                                            >
                                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                                                Approve
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-rose-500/40 text-rose-700 hover:bg-rose-500/10"
                                                onClick={() => setExtensionDecision({
                                                    req: r, mode: "deny",
                                                })}
                                                data-testid={`ext-deny-${r.id}`}
                                            >
                                                <XCircle className="h-3.5 w-3.5 mr-1" />
                                                Deny
                                            </Button>
                                        </div>
                                    ) : r.status === "approved" ? (
                                        <Badge className="bg-sky-500/15 text-sky-700 border border-sky-500/30 gap-1">
                                            <CheckCircle2 className="h-3 w-3" />
                                            Approved
                                        </Badge>
                                    ) : (
                                        <Badge className="bg-rose-500/15 text-rose-700 border border-rose-500/30 gap-1">
                                            <XCircle className="h-3 w-3" />
                                            Denied
                                        </Badge>
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Recent quiz activity */}
            {data.recent_attempts.length > 0 && (
                <Card className="border-border" data-testid="student-attempts-card">
                    <CardContent className="p-6 space-y-3">
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-accent" />
                            <h2 className="font-display text-xl text-primary">
                                Recent quiz activity
                            </h2>
                            <span className="text-xs text-muted-foreground ml-1">
                                last {data.recent_attempts.length}
                            </span>
                        </div>
                        <div className="space-y-1.5">
                            {data.recent_attempts.map((a) => {
                                const flagged = a.integrity?.flagged;
                                return (
                                    <div
                                        key={a.id}
                                        className={`flex items-center justify-between gap-3 flex-wrap rounded-md border px-3 py-2 text-sm ${
                                            flagged
                                                ? "border-amber-400/50 bg-amber-50/40"
                                                : "border-border bg-background"
                                        }`}
                                        data-testid={`attempt-row-${a.id}`}
                                    >
                                        <div className="min-w-0 flex-1 flex items-center gap-2">
                                            {a.passed ? (
                                                <CheckCircle2 className="h-3.5 w-3.5 text-sky-600 flex-shrink-0" />
                                            ) : (
                                                <XCircle className="h-3.5 w-3.5 text-rose-600 flex-shrink-0" />
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate">
                                                    <span className="text-muted-foreground text-xs">
                                                        {a.program_title} ·{" "}
                                                    </span>
                                                    <span className="font-medium">
                                                        {a.chapter_title || "Chapter"}
                                                    </span>
                                                </div>
                                                <div className="text-[11px] text-muted-foreground">
                                                    {fmtDateTime(a.created_at)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {flagged && (
                                                <Badge
                                                    variant="outline"
                                                    className="border-amber-500 text-amber-700 bg-amber-100/60 gap-1"
                                                    title={`Tab switches: ${a.integrity.tab_switch_count || 0}, window blur: ${a.integrity.window_blur_count || 0}, copy attempts: ${a.integrity.copy_attempts || 0}`}
                                                >
                                                    <ShieldAlert className="h-3 w-3" />
                                                    Flagged
                                                </Badge>
                                            )}
                                            <Badge
                                                variant="outline"
                                                className="font-mono text-[11px]"
                                            >
                                                {a.score ?? "—"}
                                            </Badge>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            <ExtensionDecisionDialog
                decision={extensionDecision}
                onClose={() => setExtensionDecision(null)}
                onSubmit={decideExtension}
            />

            <Dialog
                open={!!emailChangedDialog}
                onOpenChange={(o) => !o && setEmailChangedDialog(null)}
            >
                <DialogContent
                    className="max-w-md"
                    data-testid="email-changed-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-primary flex items-center gap-2">
                            <Mail className="h-5 w-5" />
                            Send password setup to the new email?
                        </DialogTitle>
                        <DialogDescription>
                            Sign-in email is now{" "}
                            <span className="font-mono text-foreground">
                                {emailChangedDialog?.new_email}
                            </span>
                            . The student should set a fresh password before
                            they can log in. Send a setup link now?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setEmailChangedDialog(null)}
                            data-testid="email-changed-skip"
                        >
                            Skip for now
                        </Button>
                        <Button
                            className="bg-primary hover:bg-primary/90"
                            onClick={async () => {
                                await sendReset();
                                setEmailChangedDialog(null);
                            }}
                            disabled={sendingReset}
                            data-testid="email-changed-send-reset"
                        >
                            {sendingReset ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            ) : (
                                <KeyRound className="h-3.5 w-3.5 mr-1" />
                            )}
                            Send setup link
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <StudentEnrollmentWizard
                open={wizardOpen}
                onOpenChange={setWizardOpen}
                programs={programs}
                presetEmail={data.email}
                onDone={load}
            />
        </div>
    );
}

function InfoChip({ icon: Icon, label, value, mono, capitalize, warn }) {
    return (
        <div
            className={`flex items-start gap-2 p-2 rounded-md ${
                warn ? "bg-amber-50 border border-amber-200" : "bg-muted/30"
            }`}
        >
            <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {label}
                </div>
                <div
                    className={`text-sm text-foreground truncate ${mono ? "font-mono" : ""} ${capitalize ? "capitalize" : ""}`}
                >
                    {value}
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-sm font-semibold">{label}</Label>
            {children}
        </div>
    );
}

function EnrollmentCard({ e }) {
    const win = e.access_window || {};
    const expired = win.state === "expired";
    const notStarted = win.state === "not_started";
    return (
        <div
            className={`rounded-md border px-3 py-2.5 ${
                expired
                    ? "border-amber-400/60 bg-amber-50/60"
                    : notStarted
                        ? "border-sky-400/40 bg-sky-50/60"
                        : "border-border bg-muted/20"
            }`}
            data-testid={`enrollment-card-${e.enrollment_id}`}
        >
            <div className="flex flex-col sm:flex-row items-start gap-4">
                <div className="shrink-0 self-center sm:self-start">
                    <RadialProgress
                        size={96}
                        compact
                        lessonsCompleted={e.lessons_completed || 0}
                        totalLessons={e.total_lessons || 0}
                        chaptersCompleted={e.chapters_completed || 0}
                        totalChapters={e.total_chapters || 0}
                        flagged={e.flagged_attempts || 0}
                        testId={`enrollment-ring-${e.enrollment_id}`}
                    />
                </div>
                <div className="flex-1 min-w-0 w-full">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-primary truncate">
                        {e.program_title}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        Enrolled {fmtDate(e.enrolled_at)}
                        {e.completed_at ? ` · Completed ${fmtDate(e.completed_at)}` : ""}
                        {e.payment_mode ? ` · ${e.payment_mode.replace("_", " ")}` : ""}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {expired && (
                        <Badge
                            variant="outline"
                            className="border-amber-500 text-amber-700 bg-amber-100/60"
                        >
                            Expired
                        </Badge>
                    )}
                    {notStarted && (
                        <Badge
                            variant="outline"
                            className="border-sky-500 text-sky-700 bg-sky-100/60"
                        >
                            Starts soon
                        </Badge>
                    )}
                    <Badge variant="outline" className="font-mono">
                        {e.progress_percent}%
                    </Badge>
                </div>
            </div>
            {(e.start_date || e.end_date) ? (
                <div className="text-[11px] text-muted-foreground mt-1">
                    Access: {e.start_date ? fmtDate(e.start_date) : "open"} → {e.end_date ? fmtDate(e.end_date) : "open"}
                </div>
            ) : null}
            <div className="mt-2">
                <div className="h-1.5 bg-muted rounded overflow-hidden">
                    <div
                        className="h-full bg-primary"
                        style={{ width: `${e.progress_percent}%` }}
                    />
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                    {e.chapters_completed}/{e.total_chapters} chapters passed
                </div>
            </div>
                </div>
            </div>
        </div>
    );
}

function ExtensionDecisionDialog({ decision, onClose, onSubmit }) {
    const [days, setDays] = useState(14);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (decision) {
            setDays(decision.req?.requested_days || 14);
            setNote("");
        }
    }, [decision]);

    const handle = async () => {
        setSaving(true);
        try {
            await onSubmit(decision, { days, note });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!decision} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-md"
                data-testid="ext-decision-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-primary">
                        {decision?.mode === "approve"
                            ? "Approve grace period"
                            : "Deny request"}
                    </DialogTitle>
                    <DialogDescription>
                        {decision?.req?.program_title} · requested{" "}
                        {decision?.req?.requested_days} days
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    {decision?.mode === "approve" && (
                        <div>
                            <Label className="text-sm font-semibold">
                                Approved days
                            </Label>
                            <div className="flex gap-2 mt-1.5">
                                {[7, 14, 30, 60].map((d) => (
                                    <button
                                        type="button"
                                        key={d}
                                        onClick={() => setDays(d)}
                                        className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                                            Number(days) === d
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-background border-border hover:bg-muted"
                                        }`}
                                    >
                                        {d}d
                                    </button>
                                ))}
                                <Input
                                    type="number"
                                    min={1}
                                    max={365}
                                    value={days}
                                    onChange={(e) => setDays(e.target.value)}
                                    className="w-24"
                                />
                            </div>
                        </div>
                    )}
                    <div>
                        <Label className="text-sm font-semibold">
                            Note for the student{" "}
                            <span className="font-normal text-muted-foreground">(optional)</span>
                        </Label>
                        <Textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={3}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handle}
                        disabled={saving}
                        className={
                            decision?.mode === "deny"
                                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                : "bg-primary hover:bg-primary/90"
                        }
                    >
                        {saving ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        ) : decision?.mode === "approve" ? (
                            "Approve"
                        ) : (
                            "Deny"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
